import React, {
  ChangeEvent,
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {isEqual} from 'lodash';

import {
  ProductCategoryEnum,
  ProductPackageTypeEnum,
  ProductVariant,
  Subscription,
} from '#apollo/schema';
import {
  MEMBER_LEGACY_VARIANT_TID,
  MEMBER_TID,
  MEMBER_VARIANT_TID,
  supplements,
  TESTKIT_RECUR_VARIANT_TID,
} from '#constant/products';
import {
  CustomerTypeEnum,
  getProductVariantIdByVariantTerm,
  getProductVariantIdsByTerm,
  ProductVariantTid,
  SubscriptionStatusEnum,
} from '#util/subscriptions';

import {DiscountCategory} from './components/SubscriptionManagerCard/components/UpdateConfirmationModal/UpdateConfirmationModal';
import {
  getHighestPricedSupplementTid,
  isPendingMembership,
  transformVariantToProduct,
} from './SubscriptionManager.util';

import {
  CustomerType,
  InitialSubscriptionStateProps,
  ManageProductType,
  onChangeSubscriptionProps,
  PromoCodeAction,
  SubscriptionManagerContextProps,
  SubscriptionManagerState,
  SubscriptionManagerStateType,
} from './SubscriptionManager.types';

const SubscriptionManagerContext = createContext<SubscriptionManagerContextProps | undefined>(
  undefined,
);

export const SubscriptionManagerProvider = ({children}: {children: ReactNode}) => {
  const [selectedSupplements, setSelectedSupplements] = useState<{[key: string]: boolean}>({});
  const [initialState, setInitialState] = useState<SubscriptionManagerStateType | null>(null);
  const [pendingChanges, setPendingChanges] = useState<SubscriptionManagerStateType | null>(null);
  const [productVariantsState, setProductVariantsState] = useState<ProductVariant[] | null>(null);
  const [appliedPromoCodes, setAppliedPromoCodes] = useState<string[]>([]);
  const [initialSubscriptionDiscountCodes, setInitialSubscriptionDiscountCodes] = useState<
    string[]
  >([]);
  const [discountsToSimulate, setDiscountsToSimulate] = useState<string[]>([]);
  const [openUpdateConfirmationModal, setOpenUpdateConfirmationModal] = useState(false);
  const [hasAutoAppliedPromoCodes, setHasAutoAppliedPromoCodes] = useState(false);
  const [alreadyAppliedPromoCodeError, setAlreadyAppliedPromoCodeError] = useState(false);
  const [hasAutoAppliedPromoCodesError, setHasAutoAppliedPromoCodesError] = useState(false);

  const getInitialSubscriptionManagerState = useCallback(
    ({
      subscription,
      productVariants,
    }: InitialSubscriptionStateProps): SubscriptionManagerStateType => {
      const {
        items = [],
        hasActiveSubscription,
        hasMembership,
        pausedProductVariantIds = [],
        status,
        nextBillingAttemptAt,
        id,
        discounts,
      } = subscription || {};
      setProductVariantsState(productVariants);
      const productVariantIds = items.map(item => item.variantId);
      const productVariantSets = new Set([...productVariantIds, ...pausedProductVariantIds]);

      const supplementsOnly = productVariants.filter(pv => {
        return pv.product?.category.toLowerCase() == ProductCategoryEnum.Supplement;
      });

      const bottleVariantsMap = supplementsOnly.reduce(
        (acc: {[key: string]: ProductVariant} = {}, supp) => {
          if (supp.packageType?.toLowerCase() === ProductPackageTypeEnum.Bottle) {
            acc[supp.productId] = supp;
          }
          return acc;
        },
        {},
      );

      const pouchVariantIds = supplementsOnly
        .filter(supp => supp.packageType?.toLowerCase() === ProductPackageTypeEnum.Pouch)
        .map(supp => supp.id);

      const products: ManageProductType[] =
        (Array.from(productVariantSets)
          ?.flatMap(productVariantId => {
            let variant = productVariants.find(v => v.id === productVariantId);
            const supplement = supplements.find(
              supplement => supplement.variantTid === variant?.tid,
            );

            if (variant && pouchVariantIds.includes(productVariantId)) {
              variant = bottleVariantsMap[variant.productId];
            }

            return variant && transformVariantToProduct({variant, supplement});
          })
          .filter(Boolean) as ManageProductType[]) || [];

      const pausedVariants = pausedProductVariantIds || [];
      const subscriptionStatus = status || '';
      const nextBillingDateAt = nextBillingAttemptAt || '';
      const initialProducts = products.map(product => product.variantId);
      const removeProducts: string[] = [];
      const addProducts: string[] = [];
      const subscriptionId = id || '';
      const initialDiscountCodes =
        discounts
          ?.filter(discount => discount.category.toUpperCase() === DiscountCategory.CODE)
          .map(discount => discount.title) || [];
      const addDiscountCodes: string[] = [];

      if (initialDiscountCodes.length > 0) {
        setInitialSubscriptionDiscountCodes(initialDiscountCodes);
        setDiscountsToSimulate(initialDiscountCodes);
      }

      const membershipVariantIds = getProductVariantIdsByTerm(productVariants, MEMBER_TID);

      const isPendingStatus: boolean = isPendingMembership(
        subscription as Subscription,
        !!hasMembership,
        membershipVariantIds,
      );

      const customerType: CustomerType = hasMembership
        ? CustomerTypeEnum.membership
        : CustomerTypeEnum.subscription;

      const state = isPendingStatus
        ? SubscriptionManagerState.PENDING
        : hasActiveSubscription
        ? SubscriptionManagerState.ACTIVE
        : subscriptionStatus === SubscriptionStatusEnum.PAUSED
        ? SubscriptionManagerState.PAUSED
        : SubscriptionManagerState.DESELECTED;

      const hasInitialMembership = isPendingStatus ? false : hasMembership;

      let initialSelectedSupplements = products.reduce((acc, product) => {
        if (!membershipVariantIds.includes(product.variantId)) {
          acc[product.variantId] = true;
        }
        return acc;
      }, {} as {[key: string]: boolean});

      pausedVariants.forEach(id => {
        initialSelectedSupplements[id] = true;
      });

      setSelectedSupplements(initialSelectedSupplements);

      return {
        customerType,
        state,
        benefitDiscount: 0,
        totalValue: products.reduce((total, product) => total + product.price, 0),
        products,
        hasActiveSubscription,
        hasMembership: hasInitialMembership,
        subscriptionStatus,
        nextBillingDateAt,
        initialProducts,
        removeProducts,
        addProducts,
        subscriptionId,
        addDiscountCodes,
      };
    },
    [],
  );

  const onChangeSupplement = useCallback(
    (
        supplement: ManageProductType,
        productVariants: ProductVariant[],
        customerType: CustomerType,
        subscription: Partial<Subscription>,
      ) =>
      (event: ChangeEvent<HTMLInputElement>) => {
        const {checked} = event.target;
        setSelectedSupplements(prev => ({
          ...prev,
          [supplement.variantId]: checked,
        }));
        setPendingChanges(prev => {
          if (!prev) {
            return null;
          }
          let addProducts = [...prev.addProducts];
          let removeProducts = [...prev.removeProducts];

          if (checked) {
            if (
              !prev.addProducts.includes(supplement.variantId) &&
              !prev.initialProducts.includes(supplement.variantId)
            ) {
              addProducts = [...prev.addProducts, supplement.variantId];
            }
            removeProducts = removeProducts.filter(id => id !== supplement.variantId);
          } else {
            addProducts = prev.addProducts.filter(id => id !== supplement.variantId);
            if (!prev.addProducts.includes(supplement.variantId)) {
              removeProducts = [...removeProducts, supplement.variantId];
            }
          }

          const originalSubscriptionVariants = subscription.items?.map(item => {
            return item.variantId;
          });

          const supplementsOnly = productVariants.filter(pv => {
            return pv.product?.category.toLowerCase() == ProductCategoryEnum.Supplement;
          });

          const bottleToPouchMap = supplementsOnly.reduce(
            (acc: {[key: string]: string} = {}, variant) => {
              if (variant.packageType?.toLowerCase() === ProductPackageTypeEnum.Bottle) {
                const matchingPouch = supplementsOnly.find(
                  pouch =>
                    pouch.productId === variant.productId &&
                    pouch.packageType?.toLowerCase() === ProductPackageTypeEnum.Pouch,
                );
                if (matchingPouch) {
                  acc[variant.id] = matchingPouch.id;
                }
              }
              return acc;
            },
            {},
          );

          const packageFilteredRemovedItems: string[] = [];

          removeProducts.forEach(prod => {
            const pouchVariant = bottleToPouchMap[prod];
            if (
              originalSubscriptionVariants &&
              originalSubscriptionVariants.includes(pouchVariant)
            ) {
              packageFilteredRemovedItems.push(pouchVariant);
            } else if (
              originalSubscriptionVariants &&
              originalSubscriptionVariants.includes(prod)
            ) {
              packageFilteredRemovedItems.push(prod);
            }
          });

          const updatedProducts: ManageProductType[] = checked
            ? [...prev.products, supplement]
            : prev.products.filter(product => product.variantId !== supplement.variantId);

          const highestPricedSupplementTid = getHighestPricedSupplementTid({
            selectedVariants: updatedProducts,
            activeSubscription: true,
            customerType,
            productVariants,
          });

          const finalUpdatedProducts = updatedProducts.map(product => ({
            ...product,
            membershipIncluded: highestPricedSupplementTid === product.variantId,
          }));

          return {
            ...prev,
            products: finalUpdatedProducts,
            addProducts,
            removeProducts,
          };
        });
      },
    [setSelectedSupplements, setPendingChanges],
  );

  const updateSubscriptionState = useCallback(
    (
      pendingChanges: SubscriptionManagerStateType | null,
      initialState: SubscriptionManagerStateType | null,
    ) => {
      if (!pendingChanges || !initialState) {
        return null;
      }

      const areDiscountsEqual = isEqual(initialState.addDiscountCodes, appliedPromoCodes);

      const initialProducts = initialState.products.map(product => {
        return {
          id: product.variantId,
          tid: product.tid,
        } as ProductVariantTid;
      });

      const membershipVariantId =
        getProductVariantIdByVariantTerm(initialProducts, MEMBER_VARIANT_TID) ??
        getProductVariantIdByVariantTerm(initialProducts, MEMBER_LEGACY_VARIANT_TID);

      const pendingProducts = pendingChanges.products.map(product => {
        return {
          id: product.variantId,
          tid: product.tid,
        } as ProductVariantTid;
      });

      const pendingMembershipVariantId =
        getProductVariantIdByVariantTerm(pendingProducts, MEMBER_VARIANT_TID) ??
        getProductVariantIdByVariantTerm(pendingProducts, MEMBER_LEGACY_VARIANT_TID);

      const pendingIdsMembershipRemoved = pendingChanges.products
        .filter(product => product.variantId !== pendingMembershipVariantId)
        .map(product => product.variantId)
        .sort();

      const pendingIds = pendingChanges.products.map(p => p.variantId).sort();
      const initialIds = initialState.products.map(p => p.variantId).sort();

      const hasMembershipPendingDowngrade =
        isEqual(pendingIdsMembershipRemoved, initialIds) &&
        initialState.hasMembership &&
        pendingMembershipVariantId;

      const hasSameProducts = isEqual(pendingIds, initialIds);

      const isUnsubscribedState =
        hasSameProducts &&
        initialState.state === SubscriptionManagerState.DESELECTED &&
        pendingChanges.state === SubscriptionManagerState.UPDATE;

      if (
        (hasSameProducts &&
          !isUnsubscribedState &&
          initialState.products.length === pendingChanges.products.length &&
          initialState.hasMembership === pendingChanges.hasMembership &&
          areDiscountsEqual) ||
        hasMembershipPendingDowngrade
      ) {
        setSelectedSupplements(
          initialState.products.reduce((acc, product) => {
            if (product.variantId !== membershipVariantId) {
              acc[product.variantId] = true;
            }
            return acc;
          }, {} as {[key: string]: boolean}),
        );
        return {
          ...pendingChanges,
          state: initialState.state,
        };
      }

      return {
        ...pendingChanges,
        hasActiveSubscription: true,
        state: SubscriptionManagerState.UPDATE,
        addDiscountCodes: appliedPromoCodes,
      };
    },
    [appliedPromoCodes],
  );

  const onChangeSubscription = useCallback(
    ({
      hasMembership,
      initialState,
      setPendingChanges,
      productVariants,
      customerType,
    }: onChangeSubscriptionProps) => {
      setPendingChanges(prev => {
        if (!prev || !initialState) {
          return null;
        }

        const prevProducts = prev.products.map(product => {
          return {
            id: product.variantId,
            tid: product.tid,
          } as ProductVariantTid;
        });

        const membershipVariantIds = getProductVariantIdsByTerm(productVariants, MEMBER_TID);
        const membershipVariantId =
          getProductVariantIdByVariantTerm(prevProducts, MEMBER_VARIANT_TID) ??
          getProductVariantIdByVariantTerm(prevProducts, MEMBER_LEGACY_VARIANT_TID);
        const defaultMembershipVariantId = getProductVariantIdByVariantTerm(
          productVariants,
          MEMBER_VARIANT_TID,
        );

        const testKitRecurVariantId = getProductVariantIdByVariantTerm(
          productVariants,
          TESTKIT_RECUR_VARIANT_TID,
        );

        const membershipVariant = productVariants.find(
          variant => defaultMembershipVariantId === variant.id,
        );

        const membershipProduct: ManageProductType | undefined = membershipVariant
          ? transformVariantToProduct({variant: membershipVariant, supplement: undefined})
          : undefined;

        const initialStateHasMembership = initialState.products.find(product =>
          membershipVariantIds.includes(product.variantId),
        );

        let addProducts = [...prev.addProducts];
        let removeProducts = [...prev.removeProducts];

        if (
          defaultMembershipVariantId &&
          customerType.toLowerCase() === CustomerTypeEnum.membership
        ) {
          if (!initialStateHasMembership) {
            addProducts = [...prev.addProducts, defaultMembershipVariantId];
          }
          removeProducts = removeProducts.filter(
            id => !membershipVariantIds.includes(id) && id !== testKitRecurVariantId,
          );
        }

        if (
          membershipVariantId &&
          (initialStateHasMembership || prev.addProducts.includes(membershipVariantId)) &&
          customerType.toLowerCase() === CustomerTypeEnum.subscription
        ) {
          removeProducts = initialStateHasMembership
            ? [...prev.removeProducts, membershipVariantId]
            : prev.removeProducts;
          addProducts = addProducts.filter(id => id !== membershipVariantId);
          if (testKitRecurVariantId && !removeProducts.includes(testKitRecurVariantId)) {
            removeProducts.push(testKitRecurVariantId);
          }
        }

        const updatedProducts: ManageProductType[] = hasMembership
          ? ([
              ...prev.products.filter(product => !product.membershipIncluded),
              membershipProduct,
            ].filter(Boolean) as ManageProductType[])
          : prev.products.filter(product => product.variantId !== membershipVariantId);

        const highestPricedSupplementTid = getHighestPricedSupplementTid({
          selectedVariants: updatedProducts,
          activeSubscription: true,
          customerType,
          productVariants,
        });

        const finalUpdatedProducts = updatedProducts.map(product => ({
          ...product,
          membershipIncluded: highestPricedSupplementTid === product.variantId,
        }));

        const updatedState = {
          ...prev,
          hasActiveSubscription: true,
          hasMembership: hasMembership,
          products: finalUpdatedProducts,
          addProducts,
          removeProducts,
        };

        if (
          initialState.state === SubscriptionManagerState.DESELECTED ||
          initialState.state === SubscriptionManagerState.UPDATE
        ) {
          updatedState.state = SubscriptionManagerState.UPDATE;
        }

        return updateSubscriptionState(updatedState, initialState);
      });
    },
    [updateSubscriptionState],
  );

  useEffect(() => {
    if (pendingChanges && initialState) {
      const updatedState = updateSubscriptionState(pendingChanges, initialState);
      if (updatedState && !isEqual(updatedState, pendingChanges)) {
        setPendingChanges(updatedState);
      }
    }
  }, [pendingChanges, initialState, updateSubscriptionState]);

  const selectedSupplementsCount = useMemo(() => {
    const productVariants = productVariantsState ?? [];

    return Object.keys(selectedSupplements).filter(
      key =>
        selectedSupplements[key] &&
        productVariants.find(
          pv => pv.id === key && pv.product?.category === ProductCategoryEnum.Supplement,
        ),
    ).length;
  }, [productVariantsState, selectedSupplements]);

  const updatePromoCodeSet = (
    currentSet: string[],
    promoCode: string,
    action: PromoCodeAction,
  ): string[] => {
    const updatedSet = new Set(currentSet);
    action === PromoCodeAction.ADD ? updatedSet.add(promoCode) : updatedSet.delete(promoCode);
    return Array.from(updatedSet);
  };

  const updatePromoCodes = useCallback(
    (promoCode: string, addOrRemove: PromoCodeAction) => {
      if (addOrRemove === PromoCodeAction.CLEAR) {
        setAppliedPromoCodes([]);
        setDiscountsToSimulate(initialSubscriptionDiscountCodes);
        setPendingChanges(prev => {
          if (!prev) return null;
          return {
            ...prev,
            addDiscountCodes: [],
          };
        });
        return;
      }

      setAppliedPromoCodes(prev => updatePromoCodeSet(prev, promoCode, addOrRemove));
      setDiscountsToSimulate(prev => updatePromoCodeSet(prev, promoCode, addOrRemove));

      setPendingChanges(prev => {
        if (!prev) return null;
        const updatedDiscountCodes = updatePromoCodeSet(
          prev.addDiscountCodes,
          promoCode,
          addOrRemove,
        );
        return {
          ...prev,
          addDiscountCodes: updatedDiscountCodes,
        };
      });

      setPendingChanges(prev => {
        if (!prev) return null;
        const updatedState = updateSubscriptionState(prev, initialState);
        return updatedState;
      });
    },
    [
      setAppliedPromoCodes,
      setPendingChanges,
      updateSubscriptionState,
      initialState,
      initialSubscriptionDiscountCodes,
    ],
  );

  useEffect(() => {
    if (!openUpdateConfirmationModal) {
      setOpenUpdateConfirmationModal(false);
      setHasAutoAppliedPromoCodes(false);
      setAlreadyAppliedPromoCodeError(false);
      setHasAutoAppliedPromoCodesError(false);

      // this is needed to clear wrong promo code that was auto applied
      if (hasAutoAppliedPromoCodes && hasAutoAppliedPromoCodesError) {
        updatePromoCodes('', PromoCodeAction.CLEAR);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openUpdateConfirmationModal]);

  const value = useMemo(
    () => ({
      selectedSupplements,
      setSelectedSupplements,
      selectedSupplementsCount,
      initialState,
      setInitialState,
      pendingChanges,
      setPendingChanges,
      getInitialSubscriptionManagerState,
      onChangeSubscription,
      onChangeSupplement,
      appliedPromoCodes,
      setAppliedPromoCodes,
      updatePromoCodes,
      discountsToSimulate,
      setDiscountsToSimulate,
      openUpdateConfirmationModal,
      setOpenUpdateConfirmationModal,
      hasAutoAppliedPromoCodes,
      setHasAutoAppliedPromoCodes,
      alreadyAppliedPromoCodeError,
      setAlreadyAppliedPromoCodeError,
      setHasAutoAppliedPromoCodesError,
    }),
    [
      selectedSupplements,
      selectedSupplementsCount,
      initialState,
      pendingChanges,
      getInitialSubscriptionManagerState,
      onChangeSubscription,
      onChangeSupplement,
      appliedPromoCodes,
      setAppliedPromoCodes,
      updatePromoCodes,
      discountsToSimulate,
      setDiscountsToSimulate,
      openUpdateConfirmationModal,
      setOpenUpdateConfirmationModal,
      hasAutoAppliedPromoCodes,
      setHasAutoAppliedPromoCodes,
      alreadyAppliedPromoCodeError,
      setAlreadyAppliedPromoCodeError,
      setHasAutoAppliedPromoCodesError,
    ],
  );

  return (
    <SubscriptionManagerContext.Provider value={value}>
      {children}
    </SubscriptionManagerContext.Provider>
  );
};

export const useSubscriptionManager = () => {
  const context = useContext(SubscriptionManagerContext);
  if (context === undefined) {
    throw new Error('useSubscriptionManager must be used within a SubscriptionManagerProvider');
  }
  return context;
};
